import i18n, { updatePageMetaInfo, setAppLanguage, isSupportedLanguage } from "@/lang/i18n"
import {
	Opportunity,
	type Transaction,
	type SearchFilter,
	LayerType,
	LayerTypeValue,
	Contact,
	OpportunityPriority,
	EvaluationStatus,
	type Municipality,
	type BoroughRecord,
} from "@/model/DataModel.ts"
import { defineStore } from "pinia"
import { useLocalStorage } from "@vueuse/core"
import { config, Feature } from "@/AppConfig.ts"
import { getTransactionsByInscriptionNumber, searchContactTransactions } from "@/utils/ApiClient"
import * as apiClient from "@/utils/ApiClient"
import { jwtDecode, JwtPayload } from "jwt-decode"
import { ActivityType, type Page, Unit, type OpportunityStage, type UpdateOpportunityInput, type Zone } from "@/graphql"
import type { CurrentUserBasicDetails, CurrentUserDetails, CurrentUserProject, Notification } from "@/utils/ApiClient"
import { mergeWithDefaults } from "@/utils"
import { analytics } from "@/utils/analytics"
import { isOpportunityType } from "@/utils/opportunityType"
import { supabase } from "@/utils/supabaseClient"
import { clearSessionCookie, syncSessionCookie } from "@/utils/sessionSync"
import { parseDurationToSeconds } from "@/utils/duration"
import type { ParcelExportFormat } from "@/utils/ApiClient"
import { isProjectNotification, isSearchNotification } from "@/utils/activity"
import { RANGE_MAX, TransactionVerificationStatus } from "@/types"
import type { ParcelReportContent } from "@/types/parcelReport"

interface Info {
	id?: string
	email?: string
	firstName?: string
	lastName?: string
}

type TokenInfo = Pick<Info, "id" | "email">

interface AppJwtPayload extends JwtPayload {
	permissions: string[]
	role: string
	info?: TokenInfo
}

const ValidDevelopmentTypes = Object.freeze([
	"Agricole",
	"Commercial",
	"Conservation",
	"Forestier",
	"Hotelier",
	"Inconnu",
	"Industriel",
	"Institutionnel",
	"Lotissement",
	"Mixte",
	"Résidentiel",
	"Résidentiel - Bois",
	"Résidentiel - Béton",
])

interface MapboxCameraPosition {
	zoom: number
	centerLatitude: number
	centerLongitude: number
	bearing?: number
	pitch?: number
}

// Reasons for redirecting users to the login page which we use them as query parameters
export enum RedirectReason {
	LoginRequired = "login-required",
}

export enum Permission {
	TransactionRead = "transaction.read",
	TransactionWrite = "transaction.write",
	/** @lintignore */
	OpportunityOffmarketClear = "opportunity-offmarket.clear",
	/** @lintignore */
	OpportunityTargetWrite = "opportunity-target.write",
	/** @lintignore */
	OpportunitySensitiveRead = "opportunity-sensitive.read",
	/** @lintignore */
	OpportunitySensitiveWrite = "opportunity-sensitive.write",
	/** @lintignore */
	OpportunityClientNoteWrite = "opportunity-client-note.write",
	/** @lintignore */
	ProjectWrite = "project.write",
	DevPotentialRead = "devpotential.read",
	/** @lintignore */
	DevPotentialWrite = "devpotential.write",
	/** @lintignore */
	UrbanPrioritiesWrite = "urbanpriorities.write",
	/** @lintignore */
	AuthorizationRead = "authorization.read",
	/** @lintignore */
	AuthorizationWrite = "authorization.write",
	/** @lintignore */
	ServiceTokenWrite = "service-token.write",
	/** @lintignore */
	UserCreate = "user.create",
	/** @lintignore */
	CreateTestOpportunity = "create.test.opportunity",
	/** @lintignore */
	OpportunityTargetMove = "opportunity-target.move",
	/** @lintignore */
	DocumentStoreUpload = "documentstore.upload",
	/** @lintignore */
	PlanningPolicyRead = "planning-policy.read",
	/** @lintignore */
	ProjectList = "project.list",
	/** @lintignore */
	TransactionSensitiveRead = "transaction-sensitive.read",
	/** @lintignore */
	BuyerRead = "buyer.read",
	/** @lintignore */
	ParcelExport = "parcel.export",
	/** @lintignore */
	ParcelReportExport = "parcel-report.export",
	/** @lintignore */
	ParcelSensitiveRead = "parcel-sensitive.read",
	RegulatoryAssistantUse = "regulatory-assistant.use",
	/** @lintignore */
	CrmRead = "crm.read",
	/** @lintignore */
	ContactResearchRead = "contact-research.read",
	/** @lintignore */
	OpportunityRead = "opportunity.read",
	/** @lintignore */
	ProspectsRead = "prospects.read",
	/** @lintignore */
	LandUseRead = "landuse.read",
	/** @lintignore */
	UrbanPrioritiesRead = "urbanpriorities.read",
	/** @lintignore */
	UrbanPerimetersRead = "urbanperimeters.read",
	/** @lintignore */
	ZoningRead = "zoning.read",
	/** @lintignore */
	CityPvDocumentsRead = "city-pv-documents.read",
	/** @lintignore */
	TransportationRead = "transportation.read",
	/** @lintignore */
	FloodableZonesRead = "floodablezones.read",
	/** @lintignore */
	WetlandsRead = "wetlands.read",
	/** @lintignore */
	SoilCapabilityRead = "soilcapability.read",
	/** @lintignore */
	AgriculturalZonesRead = "agriculturalzones.read",
	/** @lintignore */
	DashboardRead = "dashboard.read",
	/** @lintignore */
	EndangeredSpeciesRead = "endangered-species.read",
	/** @lintignore */
	CompletedAssessmentsRead = "completed-assessments.read",
	/** @lintignore */
	NotificationCreate = "notification.create",
	/** @lintignore */
	EmailCreate = "email.create",
	/** @lintignore */
	SmsCreate = "sms.create",
}

export enum Mode {
	Map = "map",
	Table = "table",
}

export enum TableType {
	Buyers = "buyers",
	Opportunities = "opportunities",
	Transactions = "transactions",
}

type StoredTableType = TableType.Opportunities | TableType.Transactions

type TransactionsPageCache = {
	key: string
	items: Transaction[]
	offset: number
	total: number
}

const TABLE_TYPE_STORAGE_KEY = "table-type"

export function isTableType(value: string | null | undefined): value is TableType {
	return Object.values(TableType).includes(value as TableType)
}

function isStoredTableType(value: string | null | undefined): value is StoredTableType {
	return value === TableType.Opportunities || value === TableType.Transactions
}

const AnonymousUserName = "anonymous"
const TokenRenewalWindowSeconds = parseDurationToSeconds("5m", 300)
// Anything longer than this is an old long-lived token, including the previous 99y JWTs.
const LongLivedAccessTokenThresholdSeconds = parseDurationToSeconds("90d", 7_776_000)

let activeUserApiTokenRefresh: Promise<boolean> | null = null

function decodeAppJwtPayload(token: string): AppJwtPayload | null {
	try {
		return jwtDecode<AppJwtPayload>(token)
	} catch {
		return null
	}
}

// We should check if the token is invalid, expired, close to get expired or the old long lived token so we can refresh it and get a new token
function shouldRefreshUserApiToken(
	token: string,
	nowSeconds = Math.floor(Date.now() / 1000)
): { shouldRefresh: boolean; shouldClearSessionOnFailure: boolean } {
	const payload = decodeAppJwtPayload(token)
	if (!payload?.exp) {
		return { shouldRefresh: true, shouldClearSessionOnFailure: true }
	}

	const secondsUntilExpiry = payload.exp - nowSeconds
	const expired = secondsUntilExpiry <= 0
	const nearExpiry = secondsUntilExpiry <= TokenRenewalWindowSeconds
	const legacyLongLivedToken =
		typeof payload.iat === "number" && payload.exp - payload.iat > LongLivedAccessTokenThresholdSeconds

	return {
		shouldRefresh: expired || nearExpiry || legacyLongLivedToken,
		shouldClearSessionOnFailure: expired || legacyLongLivedToken,
	}
}

// TODO move to more appropriate location? DataModel.ts likely.
export class User {
	private readonly permissions: string[]
	readonly role: string
	private readonly info?: Info

	constructor(role: string = AnonymousUserName, permissions: string[] = [], info?: Info) {
		this.role = role
		this.permissions = permissions
		this.info = info
	}

	static fromToken(token: string): User {
		const { role, permissions, info } = jwtDecode<AppJwtPayload>(token)
		const tokenInfo = info
			? {
					id: info.id,
					email: info.email,
				}
			: undefined

		return new User(role, permissions, tokenInfo)
	}

	has(p: string): boolean {
		return this.permissions.includes(p)
	}

	getInfo(): Info | undefined {
		return this.info
	}

	withInfo(info: Info): User {
		return new User(this.role, this.permissions, info)
	}

	getFullName(): string {
		if (this.info?.firstName && this.info?.lastName) {
			return `${this.info.firstName} ${this.info.lastName}`
		}
		return this.info?.firstName || this.info?.lastName || this.info?.email || "Unknown User"
	}

	getRoles(): string[] {
		return this.role === AnonymousUserName ? [] : this.role.split(",").map(r => r.trim())
	}

	isAnon(): boolean {
		return this.role === AnonymousUserName
	}
}

export type Filters = {
	layers: string[]
	devTypes: string[]
	rangeFilters: {
		superficy: { min: number; max: number }
		constructible: { min: number; max: number }
		price: { min: number; max: number }
		residentialUnits: { min: number; max: number | null }
		date: {
			min: Date | null
			max: Date | null
			dateMaxIsToday?: boolean
		}
	}
	regions: string[]
	municipalities: string[]
	boroughs: string[]
	transactionTypes: string[]
	transactionStatuses: string[]
	transactionVerificationStatus: TransactionVerificationStatus | null
	transactionSource: string | null
	transactionComposition: string | null
	transactionBuyerStatus: string | null
	transactionLandCategories: string[]
	hasContactDetails: boolean
	urbanPriorities: {
		pmad: { active: boolean; draft: boolean }
		sad: { active: boolean; draft: boolean }
		pu: { active: boolean; draft: boolean }
		ppu: { active: boolean; draft: boolean }
		pu_interview: { active: boolean; draft: boolean }
		other: { active: boolean; draft: boolean }
	}
	planningPolicy: {
		tod: boolean
		ppu: boolean
		corridor: boolean
		strategicSector: boolean
	}
	endangeredSpecies: {
		threatenedOrVulnerable: boolean
		susceptible: boolean
		candidate: boolean
		masked: boolean
	}
	developmentPotential: {
		high: boolean
		medium: boolean
		low: boolean
	}
	transitLinesStatus: {
		inUse: boolean
		inConstruction: boolean
		proposed: boolean
	}
	transitStationsStatus: {
		inUse: boolean
		inConstruction: boolean
		proposed: boolean
	}
	projectStatusFilter: Set<EvaluationStatus>
	opportunityPriorityFilter: "all" | OpportunityPriority | null
	brokerFilter: string[]
	assetTypes: string[]
	landCategoriesNew: string[]
	landSubcategories: string[]
	transactionSearch: string
}

export const defaultFilters: Filters = {
	layers: [],
	devTypes: [],
	rangeFilters: {
		superficy: { min: 0, max: RANGE_MAX },
		constructible: { min: 0, max: RANGE_MAX },
		price: { min: 0, max: RANGE_MAX },
		residentialUnits: { min: 0, max: null },
		date: {
			min: null,
			max: null,
			dateMaxIsToday: false,
		},
	},
	regions: [],
	municipalities: [],
	boroughs: [],
	transactionTypes: [],
	transactionStatuses: [],
	transactionVerificationStatus: null,
	transactionSource: null,
	transactionComposition: null,
	transactionBuyerStatus: null,
	transactionLandCategories: [],
	hasContactDetails: false,
	urbanPriorities: {
		pmad: { active: true, draft: true },
		sad: { active: true, draft: true },
		pu: { active: true, draft: true },
		ppu: { active: true, draft: true },
		pu_interview: { active: true, draft: true },
		other: { active: true, draft: true },
	},
	planningPolicy: {
		tod: true,
		ppu: true,
		corridor: true,
		strategicSector: true,
	},
	endangeredSpecies: {
		threatenedOrVulnerable: true,
		susceptible: false,
		candidate: false,
		masked: false,
	},
	developmentPotential: {
		high: true,
		medium: true,
		low: true,
	},
	transitLinesStatus: {
		inUse: true,
		inConstruction: true,
		proposed: false,
	},
	transitStationsStatus: {
		inUse: true,
		inConstruction: true,
		proposed: false,
	},
	projectStatusFilter: new Set<EvaluationStatus>(),
	opportunityPriorityFilter: null,
	brokerFilter: [],
	assetTypes: [],
	landCategoriesNew: [],
	landSubcategories: [],
	transactionSearch: "",
}

// Ajoutez ceci au début du RootStore, après les imports
const defaultOpportunitiesLayers: LayerTypeValue[] = [
	LayerType.ListingLanderz,
	LayerType.ListingPotential,
	//LayerType.OffMarketOfficial, // temporary disable to reduce noise for non internal users
	LayerType.OpportunityTarget,
]

const isInProject = new URLSearchParams(window.location.search).has("project")

// set once the current project's type resolves (after `isInProject`, which runs at module load)
let isCurrentProjectWorkspace = false

function shouldPersistFiltersLocally(): boolean {
	return !isInProject || isCurrentProjectWorkspace
}

function readStoredFilters(value: string): Filters {
	try {
		const parsed = JSON.parse(value)
		const merged = mergeWithDefaults(defaultFilters, parsed)
		merged.projectStatusFilter = new Set<EvaluationStatus>(
			Array.isArray(merged.projectStatusFilter) ? merged.projectStatusFilter : []
		)

		// the date range default is null, so mergeWithDefaults won't reconstruct it from a string
		if (typeof merged.rangeFilters.date.min === "string") {
			const parsed = new Date(merged.rangeFilters.date.min)
			merged.rangeFilters.date.min = isNaN(parsed.getTime()) ? null : parsed
		}
		if (typeof merged.rangeFilters.date.max === "string") {
			const parsed = new Date(merged.rangeFilters.date.max)
			merged.rangeFilters.date.max = isNaN(parsed.getTime()) ? null : parsed
		}

		if (merged.rangeFilters.date.dateMaxIsToday) {
			merged.rangeFilters.date.max = new Date()
		}

		merged.transitLinesStatus = {
			inUse: merged?.transitLinesStatus?.inUse ?? true,
			inConstruction: merged?.transitLinesStatus?.inConstruction ?? true,
			proposed: merged?.transitLinesStatus?.proposed ?? false,
		}

		merged.transitStationsStatus = {
			inUse: merged?.transitStationsStatus?.inUse ?? true,
			inConstruction: merged?.transitStationsStatus?.inConstruction ?? true,
			proposed: merged?.transitStationsStatus?.proposed ?? false,
		}

		return merged
	} catch (error) {
		console.error(error)
		return defaultFilters
	}
}

function readStoredVisibleLayers(value: string): string[] | null {
	try {
		const parsed = JSON.parse(value)
		return Array.isArray(parsed) ? parsed : null
	} catch {
		return null
	}
}

export function searchFilterFromFilters(filters: Filters): SearchFilter {
	return {
		opportunityTypes: filters.layers.filter(isOpportunityType),
		developmentTypes: filters.devTypes.filter((type: string) => ValidDevelopmentTypes.includes(type)),
		regions: filters.regions,
		municipalities: filters.municipalities,
		boroughs: filters.boroughs,
		price: {
			min: filters.rangeFilters.price.min,
			max: filters.rangeFilters.price.max,
		},
		landArea: {
			min: filters.rangeFilters.superficy.min,
			max: filters.rangeFilters.superficy.max,
		},
		buildableLandArea: {
			min: filters.rangeFilters.constructible.min,
			max: filters.rangeFilters.constructible.max,
		},
		residentialUnits: {
			min: filters.rangeFilters.residentialUnits.min,
			max: filters.rangeFilters.residentialUnits.max,
		},
		launchDate: {
			min: filters.rangeFilters.date.min ? filters.rangeFilters.date.min.toISOString().split("T")[0] : null,
			max: filters.rangeFilters.date.max ? filters.rangeFilters.date.max.toISOString().split("T")[0] : null,
		},
		assetTypes: filters.assetTypes,
		landCategories: filters.landCategoriesNew,
		landSubcategories: filters.landSubcategories,
		evaluationStatus:
			!isInProject && filters.projectStatusFilter.size > 0 ? Array.from(filters.projectStatusFilter) : undefined,
	} as unknown as SearchFilter
}

function normalizeBoroughsForSearch(filters: Filters, boroughRecords: BoroughRecord[]): string[] {
	const selectedBoroughs = filters.boroughs ?? []
	const selectedMunicipalities = new Set(filters.municipalities ?? [])

	if (selectedBoroughs.length === 0 || selectedMunicipalities.size === 0) {
		return selectedBoroughs
	}

	const availableBoroughs = boroughRecords
		.filter(borough => borough.municipalityName && selectedMunicipalities.has(borough.municipalityName))
		.map(borough => borough.name)

	if (availableBoroughs.length === 0) {
		return selectedBoroughs
	}

	const selected = new Set(selectedBoroughs)

	return availableBoroughs.every(borough => selected.has(borough)) ? [] : selectedBoroughs
}

export function applySearchFilterToFilters(
	filters: Filters,
	searchFilter: SearchFilter,
	canReadTransactions: boolean
): Filters {
	filters.layers = filters.layers.filter(layer => !isOpportunityType(layer)).concat(searchFilter.opportunityTypes)
	if (!canReadTransactions) {
		filters.layers = filters.layers.filter(layer => layer !== LayerType.Transaction)
	}
	filters.devTypes = searchFilter.developmentTypes
	filters.regions = searchFilter.regions
	filters.municipalities = searchFilter.municipalities
	filters.boroughs = searchFilter.boroughs
	filters.rangeFilters.price.min = searchFilter.price.min
	filters.rangeFilters.price.max = searchFilter.price.max
	filters.rangeFilters.superficy.min = searchFilter.landArea.min
	filters.rangeFilters.superficy.max = searchFilter.landArea.max
	filters.rangeFilters.constructible.min = searchFilter.buildableLandArea.min
	filters.rangeFilters.constructible.max = searchFilter.buildableLandArea.max
	filters.rangeFilters.residentialUnits.min = searchFilter.residentialUnits?.min ?? 0
	filters.rangeFilters.residentialUnits.max = searchFilter.residentialUnits?.max ?? null
	filters.rangeFilters.date.min = searchFilter.launchDate.min ? new Date(searchFilter.launchDate.min) : null
	filters.rangeFilters.date.max = searchFilter.launchDate.max ? new Date(searchFilter.launchDate.max) : null
	filters.rangeFilters.date.dateMaxIsToday = false
	filters.assetTypes = searchFilter.assetTypes || []
	filters.landCategoriesNew = searchFilter.landCategories || []
	filters.landSubcategories = searchFilter.landSubcategories || []

	return filters
}

export type MapboxCameraPositionHash =
	| `${number}/${number}/${number}`
	| `${number}/${number}/${number}/${number}`
	| `${number}/${number}/${number}/${number}/${number}`

export const UseRootStore: any = defineStore("root-store", {
	state: () => ({
		cameraPosition: useLocalStorage<MapboxCameraPosition>("cameraPosition", {} as MapboxCameraPosition),

		userApiToken: useLocalStorage("userApiToken", ""),
		isClearingSession: false,

		filters: useLocalStorage("filters", defaultFilters, {
			deep: true,
			flush: "sync",
			writeDefaults: false,
			serializer: {
				read: (value: string) => {
					if (!shouldPersistFiltersLocally()) {
						return defaultFilters
					}

					return readStoredFilters(value)
				},
				write: (value: Filters) => {
					if (!shouldPersistFiltersLocally()) {
						const existing = localStorage.getItem("filters")
						return existing || JSON.stringify({ ...defaultFilters, projectStatusFilter: [] })
					}

					return JSON.stringify({ ...value, projectStatusFilter: Array.from(value.projectStatusFilter) })
				},
			},
		}),

		// current user
		CurrentUserDetails: null as CurrentUserDetails | null,
		CurrentUserBasicDetails: null as CurrentUserBasicDetails | null,
		currentUserDetailsIncludesProjectPeople: false,

		// All transactions
		transactions: [] as Transaction[],

		// The currently selected transaction
		selectedTransaction: null as null | Transaction,

		// True while a full transaction detail is being fetched (e.g. from a map tile click)
		isSelectedTransactionLoading: false,

		// The currently previewed transaction's id (e.g. hovered)
		previewedTransaction: null as null | number,

		// The list of transactions that have been selected for export
		transactionExportList: new Map<string, Transaction>(),

		// The most recently modified transaction who's geom or map
		// representation changed
		modifiedTransaction: null as null | Transaction,

		/// all the opportunities that we received from the Landerz API
		opportunities: [] as Opportunity[],

		/// selected opportunity fetched outside the current opportunity result set
		selectedOpportunityOverride: null as Opportunity | null,
		selectedOpportunityOverrideWasAppended: false,

		/// the id of the opportunity that is currently hovered in the right menu
		hoveredOpportunityId: null as null | number,

		/// the id of the opportunity that is currently selected in the right menu
		selectedOpportunityId: null as null | number,

		// the id of the parcel that is currently selected (by clicking on the cadastre)
		selectedParcelID: null as null | string,

		// the object reprsenting the selected parcel (by clicking on the cadastre)
		selectedParcel: null as null | object,

		// the ids of the parcels that are currently multi-selected
		selectedParcelIDs: [] as number[],

		// the full objects of the parcels that are currently multi-selected
		selectedParcels: [] as object[],

		// Private state for currently displayed layers.
		// It should be read from using visibleLayers and written to
		// using setVisibleLayers.
		_visibleLayers: useLocalStorage("visibleLayers", null as string[] | null, {
			serializer: {
				read: (value: string) => {
					if (!shouldPersistFiltersLocally()) {
						return null
					}

					return readStoredVisibleLayers(value)
				},
				write: (value: string[] | null) => {
					if (!shouldPersistFiltersLocally()) {
						const current = localStorage.getItem("visibleLayers")
						return current || JSON.stringify(null)
					}

					return JSON.stringify(value)
				},
			},
		}),

		layers: [] as string[],

		currentLocale: i18n.global.locale,

		regions: [] as string[],

		municipalities: [] as string[],
		municipalityRecords: [] as Municipality[],
		boroughRecords: [] as BoroughRecord[],

		searches: {} as {
			transaction?: string
			opportunity?: string
			parcel?: string
		},

		opportunitiesPage: 1,
		transactionsPage: 1,
		transactionsPageCache: null as TransactionsPageCache | null,
		buyersListPage: 1,
		storedTableType: useLocalStorage<StoredTableType>(TABLE_TYPE_STORAGE_KEY, TableType.Opportunities, {
			writeDefaults: false,
			serializer: {
				read: (value: string) => {
					if (isStoredTableType(value)) {
						return value
					}

					return TableType.Opportunities
				},
				write: (value: StoredTableType) => value,
			},
		}),

		opportunityEvaluations: new Map<number, string | null>(),
		opportunityNotes: new Map<number, { brokerNote: string | null; clientNote: string | null }>(),
		opportunityStage: new Map<number, OpportunityStage | null>(),
		opportunityPriority: new Map<number, OpportunityPriority | null>(),
		opportunityTileRefreshKey: useLocalStorage("opportunityTileRefreshKey", 0),
		notifications: [] as Notification[],
		notificationCount: 0,
		unreadNotificationCount: 0,
		projectNotificationCount: 0,
		searchNotificationCount: 0,
		projectUnreadNotificationCount: 0,
		searchUnreadNotificationCount: 0,
		isLoadingNotifications: false,
	}),
	getters: {
		availableLayers(): LayerTypeValue[] {
			const layers: LayerTypeValue[] = [...config.filters]

			if (this.isUserLoggedIn && this.currentUser.has(Permission.OpportunityRead)) {
				layers.push(LayerType.ListingPotential, LayerType.OffMarketOfficial)
			}

			if (
				this.isUserLoggedIn &&
				this.currentUser.has(Permission.TransactionRead) &&
				config.ui.has(Feature.Transactions)
			) {
				layers.push(LayerType.Transaction)
			}

			return layers
		},

		getRegions(state): Promise<string[]> {
			// cache regions list in memory, no risk since it updates
			// exeedingly rarely
			if (state.regions.length > 1) {
				return Promise.resolve(state.regions)
			}

			return apiClient.getRegions().then(regions => {
				state.regions = regions.map(r => r.name)
				return state.regions
			})
		},

		getMunicipalities(state): Promise<string[]> {
			// cache municipality list in memory, no risk since it updates
			// exeedingly rarely
			if (state.municipalities.length > 1) {
				return Promise.resolve(state.municipalities)
			}

			return apiClient.getMunicipalities().then(munis => {
				const uniqueNames = [...new Set(munis.map(m => m.name))]
				state.municipalityRecords = munis
				state.municipalities = uniqueNames
				return state.municipalities
			})
		},

		getMunicipalityRecords(state): Promise<Municipality[]> {
			if (state.municipalityRecords.length > 1) {
				return Promise.resolve(state.municipalityRecords)
			}

			return apiClient.getMunicipalities().then(municipalities => {
				state.municipalityRecords = municipalities
				state.municipalities = [...new Set(municipalities.map(municipality => municipality.name))]

				return state.municipalityRecords
			})
		},

		getBoroughRecords(state): Promise<BoroughRecord[]> {
			if (state.boroughRecords.length > 1) {
				return Promise.resolve(state.boroughRecords)
			}

			return apiClient.getBoroughs().then(boroughs => {
				state.boroughRecords = boroughs

				return state.boroughRecords
			})
		},

		// searchFilter maps ui filters to a corresponding API entity.
		// TODO move to projectStore
		searchFilter: (state): SearchFilter => {
			return {
				...searchFilterFromFilters(state.filters),
				boroughs: normalizeBoroughsForSearch(state.filters, state.boroughRecords),
			}
		},

		// todo: "aw man that's a fucking mess" - Martin
		defaultVisibleLayers(): string[] {
			return ["opportunities", "cadastre"]
		},

		// current user projects
		userProjects(): CurrentUserProject[] {
			return this.CurrentUserDetails?.projects ?? []
		},

		getTransactionById: state => {
			return (id: number): Transaction | null =>
				state.transactions.find((t: Transaction) => Number(t.id) === id) || null
		},

		getTransactionByInscriptionNumber: state => {
			return async (inscriptionNumber: string): Promise<Transaction | null> => {
				const [transaction] = await getTransactionsByInscriptionNumber(state.userApiToken, inscriptionNumber)
				return transaction ?? null
			}
		},

		findBuyers: state => {
			return async (filters: Filters, limit: number, offset: number): Promise<{ items: Contact[]; total: number }> => {
				return await searchContactTransactions(state.userApiToken, filters, limit, offset)
			}
		},

		createBuyersList: state => {
			return async (name: string, buyerIds: string[]): Promise<{ id: string; name: string }> => {
				return await apiClient.createBuyersList(state.userApiToken, name, buyerIds)
			}
		},

		// Actual displayed layers.
		// A layer is visible if the user holds the corresponding layer.* permission.
		// Layers not in the map are unrestricted and always visible.
		visibleLayers(state): string[] {
			if (state["_visibleLayers"] == null) {
				state["_visibleLayers"] = this.defaultVisibleLayers
			}

			const layerPermissions: Partial<Record<string, Permission>> = {
				prospects: Permission.ProspectsRead,
				"planning-policy": Permission.PlanningPolicyRead,
				"urban-planning": Permission.LandUseRead,
				"land-use": Permission.LandUseRead,
				"urban-priorities": Permission.UrbanPrioritiesRead,
				"urban-perimeters": Permission.UrbanPerimetersRead,
				zoning: Permission.ZoningRead,
				"city-pv-documents": Permission.CityPvDocumentsRead,
				"transit-lines": Permission.TransportationRead,
				"transit-stations": Permission.TransportationRead,
				"floodable-zones": Permission.FloodableZonesRead,
				wetlands: Permission.WetlandsRead,
				"agricultural-inclusions-exclusions": Permission.AgriculturalZonesRead,
				"soil-capability": Permission.SoilCapabilityRead,
				"agricultural-zones": Permission.AgriculturalZonesRead,
				"endangered-species": Permission.EndangeredSpeciesRead,
				[LayerType.CompletedAssessments]: Permission.CompletedAssessmentsRead,
			}

			return (
				state["_visibleLayers"]?.filter((layer: string) => {
					const requiredPermission = layerPermissions[layer]
					if (requiredPermission) {
						return this.isUserLoggedIn && this.currentUser.has(requiredPermission)
					}
					return true
				}) ?? []
			)
		},

		opportunitiesSorted() {
			;[...this.opportunities].sort((a, b) => (a.id >= b.id ? 1 : -1))
		},

		isUserLoggedIn(state): boolean {
			return Boolean(state.userApiToken)
		},

		currentUser(): User {
			if (!this.isUserLoggedIn) {
				return new User()
			}

			const user = User.fromToken(this.userApiToken)
			const tokenInfo = user.getInfo()
			const personalInfo = this.CurrentUserBasicDetails

			return user.withInfo({
				...tokenInfo,
				firstName: personalInfo?.firstName ?? undefined,
				lastName: personalInfo?.lastName ?? undefined,
			})
		},
	},
	actions: {
		async getParcelReport(parcelId: string, content: ParcelReportContent): Promise<[Blob, string]> {
			return apiClient.createParcelReport(this.userApiToken, parcelId, content)
		},

		async getParcelExport(parcelId: string, format: ParcelExportFormat): Promise<[Blob, string]> {
			return format === "shapefile"
				? apiClient.getShapefile(this.userApiToken, parcelId)
				: apiClient.getParcelKml(this.userApiToken, parcelId)
		},

		async getParcelZoning(parcelId: string): Promise<Zone[]> {
			return apiClient.getParcelZoning(this.userApiToken, parcelId)
		},

		async getParcelSelectionExport(parcelIds: string[], format: ParcelExportFormat): Promise<[Blob, string]> {
			if (parcelIds.length === 1) {
				return this.getParcelExport(parcelIds[0], format)
			}

			return format === "shapefile"
				? apiClient.getDissolvedShapefile(this.userApiToken, parcelIds)
				: apiClient.getDissolvedParcelKml(this.userApiToken, parcelIds)
		},

		identifyCurrentUser(): void {
			const user = this.currentUser
			const info = user.getInfo()

			if (!info?.id) {
				return
			}

			analytics.identifyUser({
				id: info.id,
				email: info.email,
				name: user.getFullName(),
				role: user.role,
			})
		},

		async clearSession(): Promise<void> {
			this.isClearingSession = true
			try {
				this.userApiToken = ""
				this.CurrentUserDetails = null
				this.CurrentUserBasicDetails = null
				this.currentUserDetailsIncludesProjectPeople = false
				await clearSessionCookie().catch(() => {})
				await supabase.auth.signOut()
			} finally {
				this.isClearingSession = false
			}
		},

		async logout(): Promise<void> {
			analytics.userSignedOut()
			await this.clearSession()
		},

		async revokeSession(): Promise<void> {
			analytics.resetUser()
			await this.clearSession()
		},

		// Refresh the JWT when it is expired, close to expiring,
		// or still using the old long-lived format. Uses the current Supabase
		// session to ask the backend for a fresh JWT.
		async refreshUserApiTokenIfNeeded({ force = false }: { force?: boolean } = {}): Promise<boolean> {
			if (!this.userApiToken) {
				return false
			}

			const refreshState = shouldRefreshUserApiToken(this.userApiToken)
			if (!force && !refreshState.shouldRefresh) {
				return true
			}

			if (activeUserApiTokenRefresh) {
				return activeUserApiTokenRefresh
			}

			activeUserApiTokenRefresh = (async () => {
				try {
					const {
						data: { session },
						error,
					} = await supabase.auth.getSession()

					if (error || !session?.access_token) {
						if (refreshState.shouldClearSessionOnFailure || force) {
							await this.revokeSession()
						}
						return false
					}

					const validationResult = await apiClient.validateIDToken(session.access_token)
					if (!validationResult.success || !validationResult.token) {
						if (refreshState.shouldClearSessionOnFailure || force) {
							await this.revokeSession()
						}
						return false
					}

					this.userApiToken = validationResult.token
					await syncSessionCookie(validationResult.token)
					this.identifyCurrentUser()
					return true
				} catch (error) {
					console.error("Failed to refresh Landerz access token:", error)
					if (refreshState.shouldClearSessionOnFailure || force) {
						await this.revokeSession()
					}
					return false
				} finally {
					activeUserApiTokenRefresh = null
				}
			})()

			return activeUserApiTokenRefresh
		},

		storeTableType(tableType: TableType): void {
			if (isStoredTableType(tableType)) {
				this.storedTableType = tableType
			}
		},

		initializeDefaultLayers() {
			if (isInProject) {
				return
			}

			if (this.filters.layers.length === 0) {
				this.filters.layers = defaultOpportunitiesLayers.filter(layer => this.availableLayers.includes(layer))
			} else {
				const permissionGatedLayers: readonly string[] = [
					LayerType.ListingPotential,
					LayerType.OffMarketOfficial,
					LayerType.Transaction,
				]
				this.filters.layers = this.filters.layers.filter(
					l => !permissionGatedLayers.includes(l) || this.availableLayers.includes(l as LayerTypeValue)
				)
			}
		},
		ensureLayerSelected(layer: LayerTypeValue): void {
			if (!this.availableLayers.includes(layer) || this.filters.layers.includes(layer)) {
				return
			}

			this.filters.layers = [...this.filters.layers, layer]
		},
		// Set the state filters from a SearchFilter entity
		// TODO move to projectStore
		setFilters(sf: SearchFilter): void {
			applySearchFilterToFilters(this.filters, sf, this.currentUser.has(Permission.TransactionRead))
		},

		async fetchCurrentUserDetails(
			options: { includeProjectPeople?: boolean; includeOpportunityEvaluations?: boolean } = {}
		): Promise<void> {
			if (!this.isUserLoggedIn) return
			if (!(await this.refreshUserApiTokenIfNeeded())) return

			try {
				const currentUser =
					options.includeProjectPeople === undefined
						? await apiClient.fetchCurrentUser(this.userApiToken)
						: await apiClient.fetchCurrentUser(this.userApiToken, {
								includeProjectPeople: options.includeProjectPeople,
							})
				this.CurrentUserDetails = {
					phoneNumber: currentUser.phoneNumber,
					phoneVerifiedAt: currentUser.phoneVerifiedAt,
					preferredLanguage: currentUser.preferredLanguage,
					unit: currentUser.unit,
					projects: currentUser.projects,
				}
				this.currentUserDetailsIncludesProjectPeople = options.includeProjectPeople ?? true
				this.CurrentUserBasicDetails = {
					firstName: currentUser.firstName,
					lastName: currentUser.lastName,
					phoneNumber: currentUser.phoneNumber,
					phoneVerifiedAt: currentUser.phoneVerifiedAt,
					preferredLanguage: currentUser.preferredLanguage,
					defaultPage: currentUser.defaultPage,
					unit: currentUser.unit,
				}
				const preferredLanguage = currentUser.preferredLanguage
				if (preferredLanguage && isSupportedLanguage(preferredLanguage)) {
					this.updateLanguage(preferredLanguage, { persist: false })
				}
				await this.fetchUnreadNotificationCount()

				if (options.includeOpportunityEvaluations !== false) {
					await this.fetchOpportunityEvaluations()
				}
			} catch (error) {
				console.error("Failed to fetch current user data:", error)
				this.CurrentUserDetails = null
				this.CurrentUserBasicDetails = null
				this.currentUserDetailsIncludesProjectPeople = false
			}
		},

		async fetchBasicCurrentUserDetails(): Promise<void> {
			if (!this.isUserLoggedIn) return

			try {
				this.CurrentUserBasicDetails = await apiClient.fetchCurrentUser(this.userApiToken, { includeProjects: false })
				if (this.CurrentUserDetails) {
					this.CurrentUserDetails = {
						...this.CurrentUserDetails,
						phoneNumber: this.CurrentUserBasicDetails.phoneNumber,
						phoneVerifiedAt: this.CurrentUserBasicDetails.phoneVerifiedAt,
						preferredLanguage: this.CurrentUserBasicDetails.preferredLanguage,
						unit: this.CurrentUserBasicDetails.unit,
					}
				}
				const preferredLanguage = this.CurrentUserBasicDetails.preferredLanguage
				if (preferredLanguage && isSupportedLanguage(preferredLanguage)) {
					this.updateLanguage(preferredLanguage, { persist: false })
				}
				await this.fetchUnreadNotificationCount()
			} catch (error) {
				console.error("Failed to fetch basic current user data:", error)
				this.CurrentUserBasicDetails = null
			}
		},

		async fetchNotifications(
			options: { limit?: number; offset?: number; activityType?: ActivityType } = {}
		): Promise<void> {
			if (!this.isUserLoggedIn) return

			this.isLoadingNotifications = true
			try {
				const result = await apiClient.fetchNotifications(this.userApiToken, options)
				this.notifications = result.items

				const activityType = options.activityType ?? ActivityType.All

				switch (activityType) {
					case ActivityType.All:
						this.notificationCount = result.total
						break
					case ActivityType.Unread:
						this.unreadNotificationCount = result.total
						break
					case ActivityType.Projects:
						this.projectNotificationCount = result.total
						break
					case ActivityType.Searches:
						this.searchNotificationCount = result.total
						break
					default: {
						const unhandledActivityType: never = activityType

						throw new Error(`Unhandled activity type: ${unhandledActivityType}`)
					}
				}
			} catch (error) {
				console.error("Failed to fetch notifications:", error)
				throw error
			} finally {
				this.isLoadingNotifications = false
			}
		},

		async fetchUnreadNotificationCount(): Promise<void> {
			if (!this.isUserLoggedIn) {
				this.unreadNotificationCount = 0
				return
			}

			try {
				this.unreadNotificationCount = await apiClient.fetchUnreadNotificationCount(this.userApiToken)
			} catch (error) {
				console.error("Failed to fetch unread notification count:", error)
			}
		},

		async fetchProjectNotificationCount(): Promise<void> {
			if (!this.isUserLoggedIn) {
				this.projectNotificationCount = 0
				this.projectUnreadNotificationCount = 0

				return
			}

			try {
				const [total, unread] = await Promise.all([
					apiClient.fetchNotificationCount(this.userApiToken, ActivityType.Projects),
					apiClient.fetchNotificationCount(this.userApiToken, ActivityType.Projects, true),
				])
				this.projectNotificationCount = total
				this.projectUnreadNotificationCount = unread
			} catch (error) {
				console.error("Failed to fetch project notification count:", error)
			}
		},

		async fetchSearchNotificationCount(): Promise<void> {
			if (!this.isUserLoggedIn) {
				this.searchNotificationCount = 0
				this.searchUnreadNotificationCount = 0

				return
			}

			try {
				const [total, unread] = await Promise.all([
					apiClient.fetchNotificationCount(this.userApiToken, ActivityType.Searches),
					apiClient.fetchNotificationCount(this.userApiToken, ActivityType.Searches, true),
				])
				this.searchNotificationCount = total
				this.searchUnreadNotificationCount = unread
			} catch (error) {
				console.error("Failed to fetch search notification count:", error)
			}
		},

		async fetchActivityTabCounts(): Promise<void> {
			if (!this.isUserLoggedIn) {
				this.unreadNotificationCount = 0
				this.projectNotificationCount = 0
				this.projectUnreadNotificationCount = 0
				this.searchNotificationCount = 0
				this.searchUnreadNotificationCount = 0

				return
			}

			try {
				const counts = await apiClient.fetchActivityTabCounts(this.userApiToken)
				this.unreadNotificationCount = counts.unread
				this.projectNotificationCount = counts.projectTotal
				this.projectUnreadNotificationCount = counts.projectUnread
				this.searchNotificationCount = counts.searchTotal
				this.searchUnreadNotificationCount = counts.searchUnread
			} catch (error) {
				console.error("Failed to fetch activity tab counts:", error)
			}
		},

		async markNotificationRead(id: string): Promise<void> {
			if (!this.isUserLoggedIn) return

			const notification = await apiClient.markNotificationRead(this.userApiToken, id)
			if (!notification) return

			const index = this.notifications.findIndex(item => item.id === id)
			if (index >= 0) {
				this.notifications.splice(index, 1, notification)
			}
			this.unreadNotificationCount = Math.max(0, this.unreadNotificationCount - 1)
			if (isProjectNotification(notification.topic)) {
				this.projectUnreadNotificationCount = Math.max(0, this.projectUnreadNotificationCount - 1)
			}
			if (isSearchNotification(notification.topic)) {
				this.searchUnreadNotificationCount = Math.max(0, this.searchUnreadNotificationCount - 1)
			}
		},

		async markAllAsRead(activityType: ActivityType): Promise<void> {
			if (!this.isUserLoggedIn) {
				return
			}

			const markedCount = await apiClient.markAllAsRead(this.userApiToken, activityType)
			this.unreadNotificationCount = Math.max(0, this.unreadNotificationCount - markedCount)

			if (activityType === ActivityType.All || activityType === ActivityType.Projects) {
				this.projectUnreadNotificationCount = 0
			}
			if (activityType === ActivityType.All || activityType === ActivityType.Searches) {
				this.searchUnreadNotificationCount = 0
			}
		},

		async deleteNotification(id: string): Promise<void> {
			if (!this.isUserLoggedIn) return

			const notification = this.notifications.find(item => item.id === id)
			const deleted = await apiClient.deleteNotification(this.userApiToken, id)
			if (!deleted) return

			this.notifications = this.notifications.filter(item => item.id !== id)
			this.notificationCount = Math.max(0, this.notificationCount - 1)
			if (notification && isProjectNotification(notification.topic)) {
				this.projectNotificationCount = Math.max(0, this.projectNotificationCount - 1)
				if (!notification.readAt) {
					this.projectUnreadNotificationCount = Math.max(0, this.projectUnreadNotificationCount - 1)
				}
			}
			if (notification && isSearchNotification(notification.topic)) {
				this.searchNotificationCount = Math.max(0, this.searchNotificationCount - 1)
				if (!notification.readAt) {
					this.searchUnreadNotificationCount = Math.max(0, this.searchUnreadNotificationCount - 1)
				}
			}
			if (notification && !notification.readAt) {
				this.unreadNotificationCount = Math.max(0, this.unreadNotificationCount - 1)
			}
		},

		async fetchOpportunityEvaluations(projectId?: string): Promise<void> {
			if (!this.isUserLoggedIn) return
			try {
				const evaluations = await apiClient.fetchOpportunityEvaluations(this.userApiToken, projectId)
				this.opportunityEvaluations = new Map(evaluations.map(e => [e.opportunityId, e.status]))
				this.opportunityNotes = new Map(
					evaluations.map(e => [e.opportunityId, { brokerNote: e.brokerNote, clientNote: e.clientNote }])
				)
				this.opportunityStage = new Map(evaluations.map(e => [e.opportunityId, e.stage]))
				this.opportunityPriority = new Map(evaluations.map(e => [e.opportunityId, e.priority]))
			} catch (error) {
				console.error("Failed to fetch opportunity evaluations:", error)
			}
		},

		refreshOpportunityTiles(): void {
			this.opportunityTileRefreshKey = Math.max(Date.now(), Number(this.opportunityTileRefreshKey) + 1)
		},

		async updateOpportunityEvaluation(
			opportunityId: number,
			input: { status?: string | null; brokerNote?: string | null; clientNote?: string | null },
			projectId?: string
		): Promise<void> {
			if (!Number.isFinite(opportunityId)) return

			const previousStatus = this.opportunityEvaluations.get(opportunityId) ?? null
			const previousNotes = this.opportunityNotes.get(opportunityId) ?? { brokerNote: null, clientNote: null }
			if (input.status !== undefined) this.opportunityEvaluations.set(opportunityId, input.status)
			if (input.brokerNote !== undefined || input.clientNote !== undefined) {
				this.opportunityNotes.set(opportunityId, { ...previousNotes, ...input })
			}
			try {
				await apiClient.updateOpportunityEvaluation(this.userApiToken, opportunityId, input, projectId)
				if (input.status !== undefined) this.refreshOpportunityTiles()
			} catch (error) {
				this.opportunityEvaluations.set(opportunityId, previousStatus)
				this.opportunityNotes.set(opportunityId, previousNotes)
				throw error
			}
		},

		async updateOpportunity(input: UpdateOpportunityInput): Promise<void> {
			await apiClient.updateOpportunity(this.userApiToken, input)
		},

		updateSelectedTransaction(transaction: Transaction) {
			this.selectedTransaction?.inscriptionNumber === transaction?.inscriptionNumber
				? (this.selectedTransaction = null)
				: (this.selectedTransaction = transaction)
		},

		addSelectedTransaction(transaction: Transaction): void {
			this.selectedTransaction = transaction
		},

		clearTransactionExportList(): void {
			this.transactionExportList.clear()
		},

		// Replace list of currently displayed layers.
		setVisibleLayers(layers: string[]) {
			this["_visibleLayers"] = layers
		},

		// the store's initial read runs before the project's type is known, so re-read localStorage here
		rehydrateLocalFiltersForWorkspace(): void {
			isCurrentProjectWorkspace = true

			const storedFilters = localStorage.getItem("filters")
			if (storedFilters) {
				this.filters = readStoredFilters(storedFilters)
			}

			const storedVisibleLayers = localStorage.getItem("visibleLayers")
			if (storedVisibleLayers) {
				this["_visibleLayers"] = readStoredVisibleLayers(storedVisibleLayers)
			}
		},

		ensureVisibleLayer(layer: string): void {
			const currentLayers = this["_visibleLayers"] ?? this.defaultVisibleLayers
			if (currentLayers.includes(layer)) {
				return
			}

			this["_visibleLayers"] = [...currentLayers, layer]
		},

		async savePreferredLanguage(lang: string, options: { syncLocal?: boolean } = {}): Promise<void> {
			if (!this.isUserLoggedIn || !isSupportedLanguage(lang)) return

			const result = await apiClient.setPreferredLanguage(this.userApiToken, lang)
			const preferredLanguage = result.preferredLanguage ?? null
			if (this.CurrentUserDetails) {
				this.CurrentUserDetails = {
					...this.CurrentUserDetails,
					preferredLanguage,
				}
			}
			this.CurrentUserBasicDetails = {
				...(this.CurrentUserBasicDetails ?? {}),
				preferredLanguage,
			}

			if (options.syncLocal !== false && preferredLanguage && isSupportedLanguage(preferredLanguage)) {
				this.updateLanguage(preferredLanguage, { persist: false })
			}
		},

		async saveDefaultPage(defaultPage: Page): Promise<void> {
			if (!this.isUserLoggedIn) {
				return
			}

			const result = await apiClient.setDefaultPage(this.userApiToken, defaultPage)
			const savedDefaultPage = result.defaultPage ?? null
			this.CurrentUserBasicDetails = {
				...(this.CurrentUserBasicDetails ?? {}),
				defaultPage: savedDefaultPage,
			}
		},

		async saveUnit(unit: Unit): Promise<void> {
			if (!this.isUserLoggedIn) {
				return
			}

			const result = await apiClient.setUnit(this.userApiToken, unit)
			const savedUnit = result.unit ?? Unit.Imperial

			if (this.CurrentUserDetails) {
				this.CurrentUserDetails = {
					...this.CurrentUserDetails,
					unit: savedUnit,
				}
			}

			this.CurrentUserBasicDetails = {
				...(this.CurrentUserBasicDetails ?? {}),
				unit: savedUnit,
			}
		},

		async savePersonalInfo(firstName: string, lastName: string): Promise<void> {
			if (!this.isUserLoggedIn) {
				return
			}

			const personalInfo = await apiClient.updateCurrentUserPersonalInfo(this.userApiToken, firstName, lastName)
			this.CurrentUserBasicDetails = {
				...(this.CurrentUserBasicDetails ?? {}),
				firstName: personalInfo.firstName ?? null,
				lastName: personalInfo.lastName ?? null,
			}
		},

		updateLanguage(lang: string, options: { persist?: boolean } = {}) {
			if (!isSupportedLanguage(lang)) return

			// update all internationalized text strings within app (using t() in
			// templates)
			setAppLanguage(lang)

			// update meta information in <head> since some of it is also
			// internationalized
			updatePageMetaInfo(i18n.global.t("meta-title"), i18n.global.t("meta-description"))

			// keep user's preference across sessions
			window.localStorage.setItem("lang", lang)

			// remove lang params if it exists as it's currently only used for SEO
			const url = new URL(window.location.href)
			if (url.searchParams.has("lang")) {
				// url.searchParams.set('lang', lang)
				url.searchParams.delete("lang")
				window.history.replaceState(null, "", url.toString())
			}

			if (options.persist === false || !this.isUserLoggedIn) return

			this.savePreferredLanguage(lang, { syncLocal: false }).catch(error => {
				console.error("Failed to persist preferred language:", error)
			})
		},

		// Sets the Mapbox [camera
		// position](https://docs.mapbox.com/help/glossary/camera/), as
		// specified in a URL hash (see
		// [option.hash](https://docs.mapbox.com/mapbox-gl-js/api/map))
		//
		// Example:
		//
		//      http://localhost:8080/#12.26/45.49455/-73.60573/-144.6/54
		//
		//      12.26/45.49455/-73.60573/-144.6/54
		//        A      B         C        D   E
		//
		//      A: zoom
		//      B: center latitude
		//      C: center longitude
		//      D: bearing (optional)
		//      E: pitch (optional)
		//
		setCameraPosition(position: MapboxCameraPositionHash): void {
			const [zoom, centerLatitude, centerLongitude, bearing, pitch] = position.split("/")

			this.cameraPosition = {
				zoom: Number(zoom),
				centerLatitude: Number(centerLatitude),
				centerLongitude: Number(centerLongitude),
				bearing: bearing ? Number(bearing) : undefined,
				pitch: pitch ? Number(pitch) : undefined,
			} as MapboxCameraPosition
		},

		getCameraPositionHash(): MapboxCameraPositionHash {
			return [
				this.cameraPosition.zoom,
				this.cameraPosition.centerLatitude,
				this.cameraPosition.centerLongitude,
				this.cameraPosition.bearing,
				this.cameraPosition.pitch,
			]
				.filter(v => v != null)
				.join("/") as MapboxCameraPositionHash
		},

		setLayers(layers: string[]): void {
			this.layers = layers
		},

		async createTransaction(inscriptionNumber: string, parcelIds: string[]): Promise<Transaction> {
			const transaction = await apiClient.createTransaction(this.userApiToken, inscriptionNumber, parcelIds)
			this.transactionsPageCache = null

			window.setTimeout(() => {
				this.modifiedTransaction = transaction
			}, 1_000)

			return transaction
		},

		async createLandAssemblyTransaction(componentInscriptionNumbers: string[]): Promise<Transaction> {
			const transaction = await apiClient.createLandAssemblyTransaction(this.userApiToken, componentInscriptionNumbers)
			this.transactionsPageCache = null

			window.setTimeout(() => {
				this.modifiedTransaction = transaction
			}, 1_000)

			return transaction
		},

		async updateTransaction(inscriptionNumber: string, overrides: Partial<Transaction>): Promise<Transaction> {
			let refresh = false
			if (Object.keys(overrides).some(key => ["parcelIds", "componentInscriptionNumbers"].includes(key))) {
				refresh = true
			}

			const transaction = await apiClient.updateTransaction(this.userApiToken, inscriptionNumber, overrides)
			this.transactionsPageCache = null

			if (refresh) {
				this.modifiedTransaction = transaction
			}
			return transaction
		},

		async fetchTransactions(
			limit: number,
			offset: number,
			sortBy?: string,
			sortOrder?: "asc" | "desc"
		): Promise<{ items: Transaction[]; total: number }> {
			return await apiClient.searchTransactions(this.userApiToken, limit, offset, this.filters, sortBy, sortOrder)
		},

		async fetchOpportunity(opportunityId: string): Promise<Opportunity | null> {
			const loadedOpportunity = this.opportunities.find(
				(opportunity: Opportunity) => String(opportunity.id) === opportunityId
			)
			if (loadedOpportunity) {
				this.clearSelectedOpportunityOverride()
				this.selectedOpportunityOverride = loadedOpportunity
				return loadedOpportunity
			}

			const fetchedOpportunity = await apiClient.getOpportunity(this.userApiToken, opportunityId)
			if (!fetchedOpportunity) return null

			this.clearSelectedOpportunityOverride()
			this.opportunities = [...this.opportunities, fetchedOpportunity]
			this.selectedOpportunityOverride = fetchedOpportunity
			this.selectedOpportunityOverrideWasAppended = true

			return fetchedOpportunity
		},

		async fetchOpportunitiesByIds(opportunityIds: string[], projectId?: string | null): Promise<Opportunity[]> {
			const distinctOpportunityIds = [...new Set(opportunityIds)]

			return apiClient.getOpportunitiesByIds(this.userApiToken, distinctOpportunityIds, projectId)
		},

		// Clears the selected-opportunity override. If the override was fetched
		// outside the current result set, also removes the temporary appended row.
		clearSelectedOpportunityOverride(): void {
			if (this.selectedOpportunityOverrideWasAppended && this.selectedOpportunityOverride) {
				const selectedOpportunityOverrideId = String(this.selectedOpportunityOverride.id)
				this.opportunities = this.opportunities.filter(
					(opportunity: Opportunity) => String(opportunity.id) !== selectedOpportunityOverrideId
				)
			}

			this.selectedOpportunityOverride = null
			this.selectedOpportunityOverrideWasAppended = false
		},

		// Returns a list suitable for a map/source after ordinary filters have run.
		// This preserves the selected opportunity's visibility while it is selected,
		// even when the ordinary filter result would hide it.
		preserveSelectedOpportunityVisibility(opportunities: Opportunity[]): Opportunity[] {
			const selectedOpportunity = this.selectedOpportunityOverride
			if (!selectedOpportunity || this.selectedOpportunityId == null) return opportunities
			if (String(selectedOpportunity.id) !== String(this.selectedOpportunityId)) return opportunities
			if (opportunities.some((opportunity: Opportunity) => String(opportunity.id) === String(selectedOpportunity.id))) {
				return opportunities
			}

			return [...opportunities, selectedOpportunity]
		},

		// Updates selected-opportunity override lifetime. A pending URL/search
		// intent may temporarily clear selectedOpportunityId before the map resolves
		// the target, so keep the override while that intent exists. Once selection
		// changes or clears without a pending intent, discard the override.
		// Returns true when map source data may need to be rebuilt.
		updateSelectedOpportunityOverride(): boolean {
			const selectedOpportunity = this.selectedOpportunityOverride
			if (!selectedOpportunity) return false
			if (this.searches.opportunity != null && this.selectedOpportunityId == null) return false
			if (this.selectedOpportunityId != null && String(selectedOpportunity.id) === String(this.selectedOpportunityId)) {
				return true
			}

			this.clearSelectedOpportunityOverride()
			return true
		},

		resetFiltersToDefault(baseSearchFilter: SearchFilter | null = null) {
			if (baseSearchFilter) {
				this.filters = applySearchFilterToFilters(
					{
						...defaultFilters,
						projectStatusFilter: new Set<EvaluationStatus>(),
					},
					baseSearchFilter,
					this.currentUser.has(Permission.TransactionRead)
				)
				return
			}

			const filters = {
				...defaultFilters,
				layers: isInProject
					? defaultFilters.layers
					: defaultOpportunitiesLayers.filter(layer => this.availableLayers.includes(layer)),
				projectStatusFilter: new Set<EvaluationStatus>(),
			}

			this.filters = {
				...filters,
			}

			if (!isInProject) {
				localStorage.setItem("filters", JSON.stringify(this.filters))
			}
		},
	},
})
